// src/Activity.jsx — System → Activity (Eric, 2026-10-17). Same layout as Reporting: a report list on the left
// (collapsible groups, filter box, folds to a rail) and the selected report on the right. Gated by the
// `system.activity` Extra Permission (admins hold it implicitly).
import React, { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { Page, BlockStack, InlineStack, Text, TextField, Tooltip } from '@shopify/polaris';
import { useAuth } from './AuthComponent';
import { hasPermission, hasRole } from './constants/roles';
import { SidebarGlyph } from './AppShell';
import TimelineReport from './activity/TimelineReport';
import BriefingReadershipReport from './activity/BriefingReadershipReport';
import PickupMarksReport from './activity/PickupMarksReport';

const REPORTS = [
    { groupId: 'activity', label: 'Activity', children: [
        { slug: 'timeline', label: 'Timeline', component: TimelineReport },
        { slug: 'briefing-readership', label: 'Store Briefing Readership', component: BriefingReadershipReport },
        { slug: 'pickup-marks', label: 'Team Portal Order Pickups', component: PickupMarksReport },
    ] },
];

export default function Activity() {
    const { user } = useAuth();
    // 2026-10-20 (Eric): the "Activity Reporting" Extra Permission covers every report here (an `adminOnly`
    // flag on a report still works for anything that must stay with admins).
    const isAdmin = hasRole(user, ['admin']);
    const visibleReports = REPORTS.map(g => ({ ...g, children: g.children.filter(c => !c.adminOnly || isAdmin) }));
    const FLAT = visibleReports.flatMap(g => g.children);
    const { reportSlug } = useParams();
    const [navFilter, setNavFilter] = useState('');
    const [navCollapsed, setNavCollapsed] = useState(() => {
        try { return localStorage.getItem('activity_nav_collapsed') === '1'; } catch (_e) { return false; }
    });
    useEffect(() => {
        try { localStorage.setItem('activity_nav_collapsed', navCollapsed ? '1' : '0'); } catch (_e) { /* private window */ }
    }, [navCollapsed]);
    const [openGroups, setOpenGroups] = useState(() => {
        const out = {};
        REPORTS.forEach(g => {
            let stored = null;
            try { stored = localStorage.getItem(`activity.group.${g.groupId}`); } catch (_e) { /* ignore */ }
            out[g.groupId] = stored === null ? true : stored === '1';
        });
        return out;
    });
    const toggleGroup = useCallback((gid) => {
        setOpenGroups(prev => {
            const next = { ...prev, [gid]: !prev[gid] };
            try { localStorage.setItem(`activity.group.${gid}`, next[gid] ? '1' : '0'); } catch (_e) { /* ignore */ }
            return next;
        });
    }, []);

    if (!user) return null;
    if (!hasPermission(user, 'system.activity')) return <Navigate to="/" replace />;
    const current = FLAT.find(r => r.slug === reportSlug) || FLAT[0];
    if (!reportSlug || !FLAT.some(r => r.slug === reportSlug)) return <Navigate to={`/activity/${FLAT[0].slug}`} replace />;
    const ReportComp = current.component;
    const q = navFilter.trim().toLowerCase();

    const renderLink = (r) => {
        const active = r.slug === current.slug;
        return (
            <Link key={r.slug} to={`/activity/${r.slug}`}
                  style={{ display: 'block', textAlign: 'left', textDecoration: 'none', padding: '6px 10px 6px 22px', borderRadius: 6,
                           background: active ? '#f1f8f5' : 'transparent', color: active ? '#005c2c' : '#1a1a1a',
                           fontWeight: active ? 600 : 400, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {r.label}
            </Link>
        );
    };

    return (
        <Page fullWidth>
            <div className="reporting-outer-grid"
                 style={{ display: 'grid', gridTemplateColumns: navCollapsed ? '28px minmax(0, 1fr)' : 'clamp(210px, 18vw, 270px) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
                <div className={`reporting-sidenav${navCollapsed ? ' reporting-sidenav--folded' : ''}`}>
                    {navCollapsed ? (
                        <Tooltip content="Show the report list">
                            <button type="button" className="reporting-rail" aria-label="Show the report list" aria-expanded={false}
                                    onClick={() => setNavCollapsed(false)}>
                                <span className="reporting-rail__icon"><SidebarGlyph /></span>
                                <span className="reporting-rail__label">Activity</span>
                            </button>
                        </Tooltip>
                    ) : (
                        <div className="reporting-sidenav__panel">
                            <BlockStack gap="100">
                                <InlineStack align="space-between" blockAlign="center" wrap={false}>
                                    <Text variant="headingLg" as="h1">Activity</Text>
                                    <Tooltip content="Hide the report list">
                                        <button type="button" aria-label="Hide the report list" aria-expanded onClick={() => setNavCollapsed(true)} className="reporting-nav-hide">
                                            <SidebarGlyph />
                                        </button>
                                    </Tooltip>
                                </InlineStack>
                                <TextField labelHidden label="Filter reports" type="search" value={navFilter} onChange={setNavFilter}
                                           placeholder="Filter reports…" autoComplete="off" clearButton onClearButtonClick={() => setNavFilter('')} />
                                {visibleReports.map(g => {
                                    const kids = g.children.filter(c => !q || c.label.toLowerCase().includes(q));
                                    if (q && !kids.length) return null;
                                    const isOpen = !!q || !!openGroups[g.groupId];
                                    return (
                                        <React.Fragment key={g.groupId}>
                                            <button type="button" onClick={() => toggleGroup(g.groupId)} aria-expanded={isOpen}
                                                    style={{ textAlign: 'left', padding: '8px 10px', border: 0, borderRadius: 6, cursor: 'pointer', background: 'transparent',
                                                             color: '#1a1a1a', fontWeight: 600, fontSize: 12, letterSpacing: '0.03em', textTransform: 'uppercase',
                                                             display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <span style={{ whiteSpace: 'nowrap' }}>{g.label}</span>
                                                <span style={{ fontSize: 10, color: '#6d7175', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 120ms ease-out', display: 'inline-block' }}>▶</span>
                                            </button>
                                            {isOpen && kids.map(renderLink)}
                                        </React.Fragment>
                                    );
                                })}
                            </BlockStack>
                        </div>
                    )}
                </div>
                <div className="reporting-content">
                    <ReportComp />
                </div>
            </div>
        </Page>
    );
}
